import type { LoaderFunctionArgs, RouteObject } from 'react-router'
import { ensure, fresh, q } from '../../data/queries'
import { moduleLoader } from '../../lib/loaders'

const learner = (load: (slug: string, args: LoaderFunctionArgs) => Promise<unknown>) =>
  moduleLoader('training', (args) => load(args.params.slug ?? '', args))
const admin = (load: (slug: string, args: LoaderFunctionArgs) => Promise<unknown>) =>
  moduleLoader('training', (args) => load(args.params.slug ?? '', args), { admin: true })

/** Training module routes. Mounted under the portal shell in src/routes.tsx. URLs use the training's slug. */
export const trainingRoutes: RouteObject[] = [
  { path: 'training', lazy: async () => ({ Component: (await import('./pages/Catalog')).default }), loader: learner(() => ensure(q.trainings)) },
  {
    path: 'training/:slug',
    lazy: async () => ({ Component: (await import('./pages/TrainingLayout')).default }),
    loader: learner((slug) => ensure(q.training(slug))),
    children: [
      { index: true, lazy: async () => ({ Component: (await import('./pages/Overview')).default }) },
      { path: 'materials', lazy: async () => ({ Component: (await import('./pages/Materials')).default }) },
      { path: 'assessment', lazy: async () => ({ Component: (await import('./pages/Assessment')).default }), loader: learner((slug) => Promise.all([fresh(q.assessment(slug)), ensure(q.leaderboard(slug)).catch(() => null)])) },
    ],
  },
  { path: 'training/:slug/certificate', lazy: async () => ({ Component: (await import('./pages/CertificatePage')).default }), loader: learner((slug) => Promise.all([ensure(q.training(slug)), fresh(q.myCertificate(slug))])) },
  { path: 'training/:slug/leaderboard', lazy: async () => ({ Component: (await import('./pages/LeaderboardPage')).default }), loader: learner((slug) => Promise.all([ensure(q.training(slug)), ensure(q.leaderboard(slug)), ensure(q.trainings)])) },
  { path: 'training/:slug/results', lazy: async () => ({ Component: (await import('./pages/ResultsPage')).default }), loader: learner(async (slug) => { const t = await ensure(q.training(slug)); if (t.quiz.enabled) await ensure(q.leaderboard(slug)) }) },
  // Shareable certificate link: /certificates/<uuid>
  { path: 'certificates/:id', lazy: async () => ({ Component: (await import('./pages/CertificatePage')).SharedCertificatePage }), loader: moduleLoader('training', ({ params }) => ensure(q.certificate(params.id ?? ''))) },
]

/** Tutor / admin routes, mounted under /manage. */
export const trainingManageRoutes: RouteObject[] = [
  { index: true, lazy: async () => ({ Component: (await import('./admin/ManageTrainings')).default }), loader: admin(() => ensure(q.manage.trainings)) },
  { path: 'trainings/new', lazy: async () => ({ Component: (await import('./admin/TrainingEditor')).default }), loader: admin(() => ensure(q.manage.trainings)) },
  {
    path: 'trainings/:slug/edit',
    lazy: async () => ({ Component: (await import('./admin/TrainingEditor')).default }),
    loader: admin((slug) => Promise.all([fresh(q.manage.training(slug)), ensure(q.manage.trainings)])),
  },
  {
    path: 'trainings/:slug/results',
    lazy: async () => ({ Component: (await import('./admin/TallyPage')).default }),
    loader: admin((slug) => Promise.all([fresh(q.manage.training(slug)), fresh(q.manage.results(slug))])),
  },
]
